import {
  CohabitingFamily,
  ExcretionAssistance,
  Grade,
  HouseworkAssistance,
  MealAssistance,
  MovementAssistance,
  PayType,
  WorkType,
} from './types'

export const WelfareTypeMap = {
  SocialInsurance: 'social_insurance',
  SeverancePay: 'severance_pay',
  PayForMeal: 'pay_for_meal',
  TransportationExpenses: 'transportation_expenses',
  DrivingBenefit: 'driving_benefit',
  SevereDisabilityAllowance: 'severe_disability_allowance',
  HolidayGift: 'holiday_gift',
  CashGift: 'cash_gift',
  LongServiceGift: 'long_service_gift',
  GovernmentGrants: 'government_grants',
} as const

export const WelfareTypeLabelMap = {
  [WelfareTypeMap.TransportationExpenses]: '교통비 지원',
  [WelfareTypeMap.SocialInsurance]: '4대보험',
  [WelfareTypeMap.SeverancePay]: '퇴직급여',
  [WelfareTypeMap.PayForMeal]: '식사(비) 지원',
  [WelfareTypeMap.DrivingBenefit]: '운전 수당',
  [WelfareTypeMap.SevereDisabilityAllowance]: '중증가산수당',
  [WelfareTypeMap.HolidayGift]: '명절선물',
  [WelfareTypeMap.CashGift]: '경조사비',
  [WelfareTypeMap.LongServiceGift]: '장기근속 장려금',
  [WelfareTypeMap.GovernmentGrants]: '정부지원금',
}

export const ApplyingOptionMap = {
  Newbie: 'newbie',
  Veterant: 'veterant',
  VeterantReqired: 'veterant_required',
  DriverLicenseRequired: 'driver_license_required',
  DimentiaEducationRequired: 'dementia_education_required',
} as const

export const ApplyingOptionLabelMap = {
  [ApplyingOptionMap.Newbie]: '초보가능',
  [ApplyingOptionMap.Veterant]: '경력우대',
  [ApplyingOptionMap.VeterantReqired]: '경력필수',
  [ApplyingOptionMap.DriverLicenseRequired]: '운전면허증 필수',
  [ApplyingOptionMap.DimentiaEducationRequired]: '치매전문교육이수',
} as const

export const PayTypeMap = {
  Hourly: 'hourly',
  Daily: 'daily',
  Monthly: 'monthly',
  Yearly: 'yearly',
  PerCase: 'per_case',
} as const

export const WorkTypeMap = {
  Commute: 'commute',
  Resident: 'resident',
  BathHelp: 'bath_help',
  DayCare: 'day_care',
  Facility: 'facility',
  Sanatorium: 'sanatorium',
  Hospital: 'hospital',
  HospitalCompanion: 'hospital_companion',
} as const

export const CommuteWorkTypes: WorkType[] = [
  WorkTypeMap.Commute,
  WorkTypeMap.BathHelp,
  WorkTypeMap.Resident,
  WorkTypeMap.HospitalCompanion,
]

export const WorkTypeLabelMap = {
  [WorkTypeMap.Commute]: '방문요양',
  [WorkTypeMap.Resident]: '입주요양',
  [WorkTypeMap.BathHelp]: '방문목욕',
  [WorkTypeMap.DayCare]: '주야간보호',
  [WorkTypeMap.Sanatorium]: '요양원',
  [WorkTypeMap.Facility]: '시설요양',
  [WorkTypeMap.Hospital]: '병원',
  [WorkTypeMap.HospitalCompanion]: '병원동행',
} as const

export const PayTypeLabelMap = {
  [PayTypeMap.Hourly]: '시급',
  [PayTypeMap.Daily]: '일급',
  [PayTypeMap.Monthly]: '월급',
  [PayTypeMap.Yearly]: '연봉',
  [PayTypeMap.PerCase]: '건당',
} as const

export const HighWageMap: Record<string, { payType: PayType; wage: number }> = {
  [WorkTypeMap.Commute]: {
    payType: PayTypeMap.Hourly,
    wage: 13_000,
  },
  [WorkTypeMap.BathHelp]: {
    payType: PayTypeMap.Hourly,
    wage: 20_000,
  },
  [WorkTypeMap.Resident]: {
    payType: PayTypeMap.Monthly,
    wage: 3_400_000,
  },
  [WorkTypeMap.DayCare]: {
    payType: PayTypeMap.Monthly,
    wage: 2_200_000,
  },
  [WorkTypeMap.Sanatorium]: {
    payType: PayTypeMap.Monthly,
    wage: 2_300_000,
  },
}

export const GenderMap = {
  Male: 'male',
  Female: 'female',
  All: 'all',
} as const

export const GenderLabelMap = {
  [GenderMap.Female]: '여성',
  [GenderMap.Male]: '남성',
  [GenderMap.All]: '무관',
} as const

export const PreferredGenderLabelMap = {
  [GenderMap.All]: '상관없음',
  [GenderMap.Female]: '여성',
  [GenderMap.Male]: '남성',
} as const

export const FiveGradeMap = {
  VeryBad: 'very_bad',
  Bad: 'bad',
  Normal: 'normal',
  Good: 'good',
  VeryGood: 'very_good',
} as const

export const FiveGradeLabelMap = {
  [FiveGradeMap.VeryBad]: '매우 나쁨',
  [FiveGradeMap.Bad]: '나쁨',
  [FiveGradeMap.Normal]: '보통',
  [FiveGradeMap.Good]: '좋음',
  [FiveGradeMap.VeryGood]: '매우 좋음',
} as const

export const GradeMap = {
  None: 'none',
  First: 'first',
  Second: 'second',
  Third: 'third',
  Fourth: 'fourth',
  Fifth: 'fifth',
  Sixth: 'sixth',
} as const

export const SocialInsuranceMap = {
  EmploymentInsurance: 'employment_Insurance', // 고용보험 신고여부
  CompensationInsurance: 'compensation_insurance', // 산재보험 신고여부
  NationalInsurance: 'national_insurance', // 국민연금 신고여부
  HealthInsurance: 'health_insurance', // 건강보험 신고여부
} as const

export const RetirementPlanTypeMap = {
  DB: '확정급여형', // 확정급여형
  DC: '확정기여형', // 확정기여형
  IRP: '개인형', // 개인형
} as const

export const RetirementPlanTypeLabelMap = {
  [RetirementPlanTypeMap.DB]: '확정급여형(DB)',
  [RetirementPlanTypeMap.DC]: '확정기여형(DB)',
  [RetirementPlanTypeMap.IRP]: '개인형(IRP)',
} as const

export const JobPostingStatusMap = {
  Draft: 'draft',
  Init: 'init',
  Closed: 'closed',
  PAID: 'paid',
}

export const WorkingHoursTypeMap = {
  Regular: 'normal',
  OneRotation: 'one_shift',
  TwoRotation: 'two_shift',
  ThreeRotation: 'three_shift',
}

export const WorkingHoursTypeLabelMap = {
  [WorkingHoursTypeMap.Regular]: '평일 주간 근무',
  [WorkingHoursTypeMap.OneRotation]: '3조 1교대 근무(퐁당당)',
  [WorkingHoursTypeMap.TwoRotation]: '3조 2교대 근무(주주야야비비)',
  [WorkingHoursTypeMap.ThreeRotation]: '3조 3교대 근무',
} as const

export enum CognitiveDisorderDetailStatus {
  NoDementia = 'no_dementia',
  ShortTermMemory = 'short_term_memory',
  RoadLost = 'road_lost',
  Childish = 'childish',
  WanderingOutside = 'wandering_outside',
  NotRecognizingFamily = 'not_recognizing_family',
  DelusionOfSuspicion = 'delusion_of_suspicion',
  AggressiveBehavior = 'aggressive_behavior',
}

export const CognitiveDisorderDetailStatusLabel = {
  [CognitiveDisorderDetailStatus.NoDementia]: '정상 (치매 증상 없음)',
  [CognitiveDisorderDetailStatus.ShortTermMemory]: '했던 말을 반복하는 등 단기 기억 장애',
  [CognitiveDisorderDetailStatus.RoadLost]: '길을 잃거나 자주 가던 곳을 헤맴',
  [CognitiveDisorderDetailStatus.Childish]: '어린아이 같은 행동',
  [CognitiveDisorderDetailStatus.WanderingOutside]: '집 밖을 배회',
  [CognitiveDisorderDetailStatus.NotRecognizingFamily]: '가족을 알아보지 못함',
  [CognitiveDisorderDetailStatus.DelusionOfSuspicion]: '사람을 의심하는 망상',
  [CognitiveDisorderDetailStatus.AggressiveBehavior]: '때리거나 욕설 등 공격적인 행동',
}

export const CognitiveDisorderStatusMap = {
  NoDementia: 'no_dementia',
  EarlyStageDementia: 'early_stage_dementia',
  MidStageDementia: 'mid_stage_dementia',
  EndStageDementia: 'end_stage_dementia',
} as const

export const CognitiveDisorderStatusLabelMap = {
  [CognitiveDisorderStatusMap.NoDementia]: '정상, 치매 증상 없음',
  [CognitiveDisorderStatusMap.EarlyStageDementia]:
    '치매 초기, 했던 말을 반복하거나 사람을 의심하는 증상',
  [CognitiveDisorderStatusMap.MidStageDementia]:
    '치매 중기, 단기기억이 현저히 떨어져 길을 잃거나 요리를 못함',
  [CognitiveDisorderStatusMap.EndStageDementia]:
    '치매 말기, 중장기 기억력 및 인지능력이 저하되어 가족도 알아보지 못함',
}

export const CohabitingFamilyMap = {
  LiveAlone: 'live_alone',
  SpouseExistOnService: 'spouse_exist_on_service',
  SpouseNotExistOnService: 'spouse_not_exist_on_service',
  FamilyExistOnService: 'family_exist_on_service',
  FamilyNotExistOnService: 'family_not_exist_on_service',
} as const

export const CohabitingFamilyLabelMap = {
  [CohabitingFamilyMap.LiveAlone]: '독거 중',
  [CohabitingFamilyMap.SpouseExistOnService]: '배우자와 동거하고 있으며 서비스 시간에도 집에 있음',
  [CohabitingFamilyMap.SpouseNotExistOnService]:
    '배우자와 동거하고 있으나 서비스 시간에는 자리 비움',
  [CohabitingFamilyMap.FamilyExistOnService]:
    '다른 가족과 동거하고 있으며 서비스 시간에도 집에 있음',
  [CohabitingFamilyMap.FamilyNotExistOnService]:
    '다른 가족과 동거하고 있으나 서비스 시간에는 자리 비움',
}

export const EnvironmentOptionMap = {
  Housekeeper: 'housekeeper',
  ParkingEnabled: 'parking_enabled',
  HasPet: 'has_pet',
  Over102Sqm: 'over_102sqm',
} as const

export const EnvironmentOptionLabelMap = {
  [EnvironmentOptionMap.Housekeeper]: '가사도우미 있음',
  [EnvironmentOptionMap.ParkingEnabled]: '주차가능',
  [EnvironmentOptionMap.HasPet]: '반려동물 있음',
  [EnvironmentOptionMap.Over102Sqm]: '집 평수 30평 이상',
} as const

export const MealAssistanceMap = {
  SelfMeal: 'self_meal',
  PrepareRequired: 'prepare_required',
  CookingRequired: 'cooking_required',
  TubeFeeding: 'tube_feeding',
} as const

export const MealAssistanceLabelMap = {
  [MealAssistanceMap.SelfMeal]: '스스로 식사 가능',
  [MealAssistanceMap.PrepareRequired]: '식사 차려드리기',
  [MealAssistanceMap.CookingRequired]: '죽, 반찬 등 요리 필요',
  [MealAssistanceMap.TubeFeeding]: '경관식 보조',
}

export const MealAssistanceNewLineLabelMap = {
  [MealAssistanceMap.SelfMeal]: '스스로 식사 가능',
  [MealAssistanceMap.PrepareRequired]: '식사 차려드리기',
  [MealAssistanceMap.CookingRequired]: '죽, 반찬 등\n요리 필요',
  [MealAssistanceMap.TubeFeeding]: '경관식 보조',
}

export const ExcretionAssistanceMap = {
  SelfExcretion: 'self_excretion',
  HelpRequired: 'help_required',
  UseDiaper: 'use_diaper',
  IndwellingCatheter: 'indwelling_catheter',
} as const

export const ExcretionAssistanceLabelMap = {
  [ExcretionAssistanceMap.SelfExcretion]: '스스로 배변 가능',
  [ExcretionAssistanceMap.HelpRequired]: '가끔 대소변 실수 시 도움',
  [ExcretionAssistanceMap.UseDiaper]: '기저귀 케어필요',
  [ExcretionAssistanceMap.IndwellingCatheter]: '유치도뇨/방광루/장루 관리',
} as const

export const ExcretionAssistanceNewLineLabelMap = {
  [ExcretionAssistanceMap.SelfExcretion]: '스스로 배변 가능',
  [ExcretionAssistanceMap.HelpRequired]: '가끔 대소변 실수 시 도움',
  [ExcretionAssistanceMap.UseDiaper]: '기저귀 케어\n필요',
  [ExcretionAssistanceMap.IndwellingCatheter]: '유치도뇨/방광\n루/장루 관리',
} as const

export const HouseworkAssistanceMap = {
  Cleaning: 'cleaning',
  Bath: 'bath',
  GoingHospital: 'going_hospital',
  WalkExercise: 'walk_exercise',
  Crony: 'crony',
  CognitiveSimulationTraining: 'cognitive_stimulation_training',
} as const

export const HouseworkAssistanceLabelMap = {
  [HouseworkAssistanceMap.Cleaning]: '청소,빨래 보조(어르신 중심)',
  [HouseworkAssistanceMap.Bath]: '목욕 보조',
  [HouseworkAssistanceMap.GoingHospital]: '병원 동행',
  [HouseworkAssistanceMap.WalkExercise]: '산책, 간단한 운동',
  [HouseworkAssistanceMap.Crony]: '말벗 등 정서지원',
  [HouseworkAssistanceMap.CognitiveSimulationTraining]: '인지자극 활동',
} as const

export const ApplyingMethodMap = {
  Phone: 'phone',
  Email: 'email',
  Visiting: 'visiting',
  CarePartnerSpecial: 'carepartner_special',
} as const

export const ApplyingMethodLabelMap = {
  [ApplyingMethodMap.Phone]: '전화지원',
  [ApplyingMethodMap.Email]: '이메일지원',
  [ApplyingMethodMap.Visiting]: '방문지원',
}

export const ApplyingDueDateMap = {
  OneWeek: 'one_week',
  TwoWeeks: 'two_weeks',
  ThreeWeeks: 'three_weeks',
  ThreeDays: 'three_days',
} as const

export const ApplyingDueDateLabelMap = {
  [ApplyingDueDateMap.OneWeek]: '1주',
  [ApplyingDueDateMap.TwoWeeks]: '2주',
} as const

export const DaysMap = {
  [ApplyingDueDateMap.OneWeek]: 7,
  [ApplyingDueDateMap.TwoWeeks]: 14,
  [ApplyingDueDateMap.ThreeWeeks]: 21,
  [ApplyingDueDateMap.ThreeDays]: 3,
} as const

export const MovementAssistanceMap = {
  SelfMovement: 'self_movement',
  HelpRequired: 'help_required',
  Wheelchair: 'wheelchair',
  Invalidity: 'invalidity',
} as const

export const MovementAssistanceLabelMap = {
  [MovementAssistanceMap.SelfMovement]: '스스로 거동 가능',
  [MovementAssistanceMap.HelpRequired]: '이동시 부축 도움',
  [MovementAssistanceMap.Wheelchair]: '휠체어 이동 보조',
  [MovementAssistanceMap.Invalidity]: '거동 불가',
} as const

export const MovementAssistanceNewLineLabelMap = {
  [MovementAssistanceMap.SelfMovement]: '스스로 거동 가능',
  [MovementAssistanceMap.HelpRequired]: '이동시 부축 도움',
  [MovementAssistanceMap.Wheelchair]: '휠체어\n이동 보조',
  [MovementAssistanceMap.Invalidity]: '거동 불가',
} as const

export const SurveyStatusMap = {
  Hiring: 'hiring',
  Finished: 'finished',
  Terminate: 'terminate',
  ScheduledInterview: 'scheduled_interview',
} as const

export const SurveyStatusLabelMap = {
  [SurveyStatusMap.Hiring]: '아직 채용중이에요',
  [SurveyStatusMap.Finished]: '채용을 완료했어요',
  [SurveyStatusMap.Terminate]: '채용을 중단했어요',
  [SurveyStatusMap.ScheduledInterview]: '면접날짜를 잡았어요',
} as const

export const AgeRangeMap = {
  Twenties: 'Twenties',
  Thirties: 'Thirties',
  Fourties: 'Fourties',
  Fifties: 'Fifties',
  Sixties: 'Sixties',
  Seventies: 'Seventies',
  Eighties: 'Eighties',
} as const

export const AgeRangeLabelMap = {
  [AgeRangeMap.Twenties]: '20대',
  [AgeRangeMap.Thirties]: '30대',
  [AgeRangeMap.Fourties]: '40대',
  [AgeRangeMap.Fifties]: '50대',
  [AgeRangeMap.Sixties]: '60대',
  [AgeRangeMap.Seventies]: '70대',
  [AgeRangeMap.Eighties]: '80대',
} as const

export const DifficultGrades: readonly (Grade | any)[] = [GradeMap.First, GradeMap.Second] as const
export const DifficultExcretionAssistances: readonly (ExcretionAssistance | any)[] = [
  ExcretionAssistanceMap.IndwellingCatheter,
  ExcretionAssistanceMap.UseDiaper,
] as const

export const DifficultHouseworkAssistances: readonly (HouseworkAssistance | any)[] = [
  HouseworkAssistanceMap.Cleaning,
  HouseworkAssistanceMap.GoingHospital,
  HouseworkAssistanceMap.Bath,
] as const

export const DifficultMealAssistances: readonly (MealAssistance | any)[] = [
  MealAssistanceMap.CookingRequired,
  MealAssistanceMap.TubeFeeding,
] as const

export const DifficultMovementAssistances: readonly (MovementAssistance | any)[] = [
  MovementAssistanceMap.Invalidity,
  MovementAssistanceMap.Wheelchair,
] as const

export const DifficultCohabitingFamily: readonly (CohabitingFamily | any)[] = [
  CohabitingFamilyMap.FamilyExistOnService,
  CohabitingFamilyMap.SpouseExistOnService,
] as const
